import React, { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import {
    Box,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Divider,
    Grid,
    Button,
    Paper,
    IconButton,
    Tooltip,
} from "@mui/material";
import HeaderBold from "../../../Layout/Header-Bold";
import Close from '@mui/icons-material/Close';
import Printicon from "@mui/icons-material/Print";
import ResponsiveBox from "../../../Layout/ResponsiveScrollBox";

/**
 * Updated SalesOrderReport:
 * - Adds centered "Confirmation Order" header
 * - Confirmation No/Date boxed at right
 * - Full-width Remarks area
 * - Right-aligned summary card for totals (fixed alignment)
 * - Print button hidden in print media
 */
const SalesOrderReport = ({ orderData, handleBackToSalesOrderDetails, totals }) => {
    const reportRef = useRef();

    const handlePrint = useReactToPrint({
        content: () => reportRef.current,
    });

    // small helper to format labels/values in summary
    const SummaryRow = ({ label, value }) => (
        <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
            <Typography variant="body2">{label}</Typography>
            <Typography variant="body2" sx={{ ml: 2 }}>{value}</Typography>
        </Box>
    );


    // const inv = orderData?.OrderInvoices?.[0] || {};
    const items = orderData?.OrderInvoiceItems || [];
    const inv = totals;
    const assessableValue = items.reduce(
        (sum, item) => sum + (Number(item?.SIItemTotalAmount) || 0),
        0
    );

    // Build rows dynamically (like your Crystal Report logic)
    const chargeRows = [];

    if (Number(inv.discountAmount) > 0) {
        chargeRows.push({ header: "Discount Value", amount: inv.discountAmount });
    }

    if (Number(inv.insuranceAmount) > 0) {
        chargeRows.push({ header: `Insurance @ ${inv?.insuranceRatePct}` || "Insurance", amount: inv.insuranceAmount });
    }

    if (Number(inv.freightAmount) > 0) {
        chargeRows.push({ header: `Freight @ ${inv?.freightAmountPerMT} /1000` || "Freight", amount: inv.freightAmount });
    }

    if (Number(inv.sChargeAmount) > 0) {
        chargeRows.push({ header: `Service Charges @ ${inv?.serviceChargePct}` || "Service Charges", amount: inv.sChargeAmount });
    }

    if (inv.totalAmount > 0) {
        chargeRows.push({ header: "Assessable Value", amount: inv.totalAmount });
    }

    if (Number(inv.igstamount) > 0) {
        chargeRows.push({ header: `IGST @ ${inv?.igstpct}` || "IGST", amount: inv.igstamount });
    }

    if (Number(inv.sgstamount) > 0) {
        chargeRows.push({ header: `SGST @ ${inv?.sgstpct}` || "SGST", amount: inv.sgstamount });
    }

    if (Number(inv.cgstamount) > 0) {
        chargeRows.push({ header: `CGST @ ${inv?.cgstpct}` || "CGST", amount: inv.cgstamount });
    }

    // Round off always included
    chargeRows.push({ header: "Round Off", amount: inv.roundOff || 0 });


    // format date 
    const formatDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        if (isNaN(date)) return dateString; // if invalid, return as-is

        const day = String(date.getDate()).padStart(2, "0");
        const month = date.toLocaleString("en-US", { month: "short" }); // Jan, Feb, ...
        const year = date.getFullYear();

        return `${day}-${month}-${year}`;
    };

    let ReportStatus=orderData?.OrderInvoices?.[0]?.OrderStatus==='New'? 'DRAFT':''

 
    return (
        <>
            <Grid container justifyContent="space-between" alignItems="center" padding={0}>
                {/* Left-aligned: Edit Employee */}
                <Grid item>
                    <Typography variant="h4" sx={{ textAlign: "center", width: "100%", marginLeft: "10px", alignItems: "center" }}>
                        Print Sales Order Report
                    </Typography>
                </Grid>

                {/* Right-aligned: Buttons */}
                <Grid item>
                    <Grid container alignItems="center">
                        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                        {/* Save */}
                        <Grid item ml={1} mr={1}>
                            <Tooltip title="Add new orders">
                                <IconButton
                                    onClick={() => handlePrint()}
                                    sx={{ display: 'flex', alignItems: 'center' }}
                                // type="submit"
                                >
                                    <Printicon style={{ fontSize: "18px", color: "#009688" }} />
                                    <Typography variant="body2" sx={{ ml: 1 }}> Print</Typography>
                                </IconButton>
                            </Tooltip>
                        </Grid>

                        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                        <IconButton onClick={handleBackToSalesOrderDetails}>
                            <Close style={{ fontSize: "14px", color: "#2196F3" }} />
                        </IconButton>

                        <Divider orientation="vertical" flexItem />

                    </Grid>
                </Grid>
            </Grid>

            <Divider />
            <Box>
                
                <ResponsiveBox
                    customMaxHeight={{
                        xs: '60vh',  // Custom value for extra small devices
                        sm: '65vh',  // Custom value for small devices
                        md: '70vh',  // Custom value for medium devices
                        lg: '84vh',  // Custom value for large devices
                        xl: '89.6vh',  // Custom value for extra large devices
                    }}>

                    {/* Report content sized for A4 */}
                    <Paper
                        ref={reportRef}
                        elevation={0}
                        sx={{
                            p: 3,
                            bgcolor: "#fff",
                            width: "210mm",            // A4 width for print
                            minHeight: "297mm",
                            mx: "auto",
                            color: "#000",
                            mt: 2,
                            mb:2

                        }}
                    >
                        {/* Company header */}
                        <Grid container alignItems="center" sx={{ borderBottom: "1px solid #000", pb: 1 }}>
                            {/* Left corner: Report Type */}
                            <Grid item xs={3} sx={{ textAlign: "left" }}>
                                <Box
                                    component="img"
                                    src="" // 👉 put your FSC/logo image path here
                                    alt="Company Logo"
                                    sx={{ width: 80, height: "auto" }}
                                />
                            </Grid>

                            {/* Center: Company Info */}
                            <Grid item xs={6} sx={{ textAlign: "center" }}>
                                <Typography variant="h6" fontWeight="bold">
                                    {orderData?.OrderInvoices?.[0]?.ICompanyName}
                                </Typography>

                                <Typography variant="body2">
                                    {orderData?.OrderInvoices?.[0]?.ICompanyAddrLine1}
                                    {orderData?.OrderInvoices?.[0]?.ICompanyAddrLine2 ? `, ${orderData?.OrderInvoices?.[0]?.ICompanyAddrLine2}` : ""}
                                    {orderData?.OrderInvoices?.[0]?.ICompanyAddrLine3 ? `, ${orderData?.OrderInvoices?.[0]?.ICompanyAddrLine3}` : ""}
                                    {orderData?.OrderInvoices?.[0]?.ICompanyAddrLine4 ? `, ${orderData?.OrderInvoices?.[0]?.ICompanyAddrLine4}` : ""}
                                    {orderData?.OrderInvoices?.[0]?.ICompanyAddrPincode ? ` PIN:${orderData?.OrderInvoices?.[0]?.ICompanyAddrPincode}` : ""}
                                </Typography>

                                <Typography variant="body2">
                                    {orderData?.OrderInvoices?.[0]?.ICompanyAddrState}{" "}
                                    {orderData?.OrderInvoices?.[0]?.ICompanyAddrCountry
                                        ? ` ${orderData?.OrderInvoices?.[0]?.ICompanyAddrCountry}`
                                        : ""}
                                    {orderData?.OrderInvoices?.[0]?.ICompanyAddrEmailId
                                        ? ` EMAIL ID: ${orderData?.OrderInvoices?.[0]?.ICompanyAddrEmailId}`
                                        : ""}
                                </Typography>

                                <Typography variant="body2">
                                    GSTIN: {orderData?.OrderInvoices?.[0]?.ICompanyGSTNo}
                                </Typography>
                            </Grid>

                            {/* Right corner: Version / R1 */}
                            <Grid item xs={3} sx={{ textAlign: "right" }}>
                                <Typography variant="body2" fontWeight="bold">
                                    {/* {orderData?.OrderInvoices?.[0]?.SOrderStatus || "R1"} */}
                                    {ReportStatus}
                                </Typography>
                            </Grid>
                        </Grid>                 



                        {/* Item Table */}
                        <Table
                            size="small"
                            sx={{ border: "1px solid #000", borderCollapse: "collapse", width: "100%" }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        colSpan={12}
                                        align="center"

                                        sx={{ border: "1px solid #000", fontWeight: "bold", fontSize: "16px", color: "black" }}
                                    >
                                        Confirmation Order
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {/* Bill-To / Ship-To / Confirmation */}

                                <TableRow>
                                    {/* Bill-To */}
                                    <TableCell
                                        sx={{ border: "1px solid #000", p: "4px", color: "black" }}
                                        colSpan={4}
                                        valign="top"
                                    >
                                        <b>Bill-To :</b><br />
                                        {orderData?.OrderInvoices?.[0]?.IBillToName || ''}<br />
                                        {orderData?.OrderInvoices?.[0]?.IBillToAddrLine1 || ''}<br />
                                        {orderData?.OrderInvoices?.[0]?.IBillToAddrLine2 || ''}<br />
                                        {orderData?.OrderInvoices?.[0]?.IBillToAddrCity || ''}<br />
                                        {orderData?.OrderInvoices?.[0]?.IBillToAddrState || ''}<br />
                                        {orderData?.OrderInvoices?.[0]?.IBillToAddrCountry || ''} - {orderData?.OrderInvoices?.[0]?.IBillToAddrPincode || ''}<br />
                                        TEL: {orderData?.OrderInvoices?.[0]?.IBillToAddrTelNo1 || ''}<br />
                                        EMAIL: {orderData?.OrderInvoices?.[0]?.IBillToAddrEmailId1 || ''}<br />
                                        GSTIN: {orderData?.OrderInvoices?.[0]?.IBillToGSTNo || ''}<br />
                                        State: {orderData?.OrderInvoices?.[0]?.IBillToAddrState || ''}<br />
                                        Code: {orderData?.OrderInvoices?.[0]?.IBillToGSTStateCode || ''}
                                    </TableCell>


                                    {/* Ship-To */}
                                    <TableCell
                                        sx={{ border: "1px solid #000", p: "4px", color: "black" }}
                                        colSpan={4}
                                        valign="top"
                                    >
                                        <b>Ship-To :</b><br />
                                        {orderData?.OrderInvoices?.[0]?.IShipToName || ''}<br />
                                        {orderData?.OrderInvoices?.[0]?.IShipToAddrLine1 || ''}<br />
                                        {orderData?.OrderInvoices?.[0]?.IShipToAddrLine2 || ''}<br />
                                        {orderData?.OrderInvoices?.[0]?.IShipToAddrCity || ''}<br />
                                        {orderData?.OrderInvoices?.[0]?.IShipToAddrState || ''}<br />
                                        {orderData?.OrderInvoices?.[0]?.IShipToAddrCountry || ''} - {orderData?.OrderInvoices?.[0]?.IShipToAddrPincode || ''}<br />
                                        TEL: {orderData?.OrderInvoices?.[0]?.IShipToAddrTelNo1 || ''}<br />
                                        EMAIL: {orderData?.OrderInvoices?.[0]?.IShipToAddrEmailId1 || ''}<br />
                                        GSTIN: {orderData?.OrderInvoices?.[0]?.IShipToGSTNo || ''}<br />
                                        State: {orderData?.OrderInvoices?.[0]?.IShipToAddrState || ''}<br />
                                        Code: {orderData?.OrderInvoices?.[0]?.IShipToGSTStateCode || ''}
                                    </TableCell>


                                    {/* Confirmation Details */}
                                    <TableCell
                                        sx={{
                                            border: "1px solid #000",
                                            p: "4px",
                                            verticalAlign: "top",   // ✅ ensures text sticks to top
                                            color: "black"
                                        }}
                                        colSpan={4}
                                    >
                                        <div style={{ margin: 0, padding: 0, lineHeight: "1.4" }}>
                                            <b>Confirmation No:</b> {orderData?.OrderInvoices?.[0]?.IConfirmationOrderNo || ''}<br />
                                            <b>Confirmation Date:</b> {formatDate(orderData?.OrderInvoices?.[0]?.IConfirmationOrderDate)}<br />
                                            <b>Party PO No:</b> {orderData?.OrderInvoices?.[0]?.PartyPONo || ""}<br />
                                            <b>Party PO Date:</b>{formatDate(orderData?.OrderInvoices?.[0]?.PODate)} <br />
                                            <b>Delivery Area:</b> {orderData?.OrderInvoices?.[0]?.DeliveryArea || ""}
                                        </div>
                                    </TableCell>

                                </TableRow>

                                {/* Item Table Header */}
                                <TableRow sx={{ bgcolor: "#eaeaea", color: "black" }}>
                                    <TableCell sx={{ border: "1px solid #000", textAlign: "center", p: "2px 4px", fontWeight: "bold", width: "40px", color: "black" }}>
                                        S. No
                                    </TableCell>
                                    <TableCell sx={{ border: "1px solid #000", p: "2px 4px", fontWeight: "bold", width: "100px", color: "black" }}>
                                        Variety
                                    </TableCell>
                                    <TableCell sx={{ border: "1px solid #000", textAlign: "center", p: "2px 4px", fontWeight: "bold", width: "50px", color: "black" }}>
                                        GSM
                                    </TableCell>
                                    <TableCell sx={{ border: "1px solid #000", textAlign: "center", p: "2px 4px", fontWeight: "bold", width: "50px", color: "black" }}>
                                        Unit
                                    </TableCell>
                                    <TableCell sx={{ border: "1px solid #000", p: "2px 4px", fontWeight: "bold", width: "150px", color: "black" }}>
                                        Size
                                    </TableCell>
                                    <TableCell sx={{ border: "1px solid #000", textAlign: "center", p: "2px 4px", fontWeight: "bold", width: "70px", color: "black" }}>
                                        No of Reels
                                    </TableCell>
                                    <TableCell sx={{ border: "1px solid #000", textAlign: "center", p: "2px 4px", fontWeight: "bold", width: "80px", color: "black" }}>
                                        Total Weight
                                    </TableCell>
                                    <TableCell sx={{ border: "1px solid #000", textAlign: "center", p: "2px 4px", fontWeight: "bold", width: "50px", color: "black" }}>
                                        UoM
                                    </TableCell>
                                    <TableCell sx={{ border: "1px solid #000", p: "2px 4px", fontWeight: "bold", width: "160px", color: "black" }}>
                                        Remarks
                                    </TableCell>
                                    <TableCell sx={{ border: "1px solid #000", textAlign: "center", p: "2px 4px", fontWeight: "bold", width: "80px", color: "black" }}>
                                        Billing Rate (INR)
                                    </TableCell>
                                    <TableCell sx={{ border: "1px solid #000", textAlign: "center", p: "2px 4px", fontWeight: "bold", width: "80px", color: "black" }}>
                                        Unit Price (INR)
                                    </TableCell>
                                    <TableCell sx={{ border: "1px solid #000", textAlign: "center", p: "2px 4px", fontWeight: "bold", width: "100px", color: "black" }}>
                                        Total Value (INR)
                                    </TableCell>
                                </TableRow>



                                {orderData?.OrderInvoiceItems?.map((item, index) => (
                                    <TableRow key={index}>
                                        <TableCell
                                            sx={{ border: "1px solid #000", textAlign: "center", p: "2px 4px", lineHeight: 1.2, fontSize: "12px", color: "black" }}
                                        >
                                            {index + 1}
                                        </TableCell>
                                        <TableCell sx={{ border: "1px solid #000", p: "2px 4px", fontSize: "12px", color: "black" }}>{item?.SIName}</TableCell>
                                        <TableCell sx={{ border: "1px solid #000", p: "2px 4px", fontSize: "12px", color: "black" }}>{item?.SIGSM}</TableCell>
                                        <TableCell sx={{ border: "1px solid #000", p: "2px 4px", fontSize: "12px", color: "black",textAlign: "center" }}>{item?.SIUnit || ''}</TableCell>
                                        <TableCell sx={{ border: "1px solid #000", p: "2px 4px", fontSize: "12px", color: "black",textAlign: "center" }}>
                                            {`${item?.SIWidth} X ${item?.SILength}`}
                                        </TableCell>
                                        <TableCell sx={{ border: "1px solid #000", textAlign: "right", p: "2px 4px", fontSize: "12px", color: "black" }}>
                                            {item?.SIReels || ''}
                                        </TableCell>
                                        <TableCell sx={{ border: "1px solid #000", textAlign: "right", p: "2px 4px", fontSize: "12px", color: "black" }}>
                                            {item?.SIConfirmed_Qty}
                                        </TableCell>
                                        <TableCell sx={{ border: "1px solid #000", p: "2px 4px", fontSize: "12px", color: "black" ,textAlign: "center"}}>{item?.SIUnitOfMeasure}</TableCell>
                                        <TableCell sx={{ border: "1px solid #000", p: "2px 4px", fontSize: "12px", color: "black" }}>{item?.Remarks || ''}</TableCell>
                                        <TableCell sx={{ border: "1px solid #000", textAlign: "right", p: "2px 4px", fontSize: "12px", color: "black" }}>
                                            {item?.SIBillingPrice}
                                        </TableCell>
                                        <TableCell sx={{ border: "1px solid #000", textAlign: "right", p: "2px 4px", fontSize: "12px", color: "black" }}>
                                            {item?.SIUnitPrice}
                                        </TableCell>
                                        <TableCell sx={{ border: "1px solid #000", textAlign: "right", p: "2px 4px", fontSize: "12px", color: "black" }}>
                                            {item?.SIItemTotalAmount}
                                        </TableCell>
                                    </TableRow>
                                ))}



                                {/* TOTAL Row */}
                                <TableRow sx={{ bgcolor: "#f9f9f9" }}>
                                    <TableCell sx={{ border: "1px solid #000", color: "black" }} colSpan={6}>
                                        <b>TOTAL</b>
                                    </TableCell>

                                    {/* Total Weight */}
                                    <TableCell sx={{ border: "1px solid #000",textAlign: "right", color: "black" }}>                                       
                                        {inv?.totalQtyKg || "0.00"}
                                    </TableCell>

                                    {/* Take UOM from the first item, or fallback to KG */}
                                    <TableCell sx={{ border: "1px solid #000", color: "black", textAlign: "center" }}>
                                        {orderData?.OrderInvoiceItems?.[0]?.SIUnitOfMeasure || "KG"}
                                    </TableCell>

                                    <TableCell sx={{ border: "1px solid #000", color: "black" }}></TableCell>
                                    <TableCell sx={{ border: "1px solid #000", color: "black" }}></TableCell>
                                    <TableCell sx={{ border: "1px solid #000", color: "black" }}></TableCell>

                                    {/* Sum of SIItemTotalAmount */}
                                    <TableCell sx={{ border: "1px solid #000", textAlign: "right", color: "black" }}>
                                        {orderData?.OrderInvoiceItems?.reduce(
                                            (sum, item) => sum + (Number(item?.SIItemTotalAmount) || 0),
                                            0
                                        )}
                                    </TableCell>
                                </TableRow>



                                {/* Remarks + Summary */}
                                <TableRow>
                                    <TableCell
                                        sx={{ border: "1px solid #000", p: "4px", verticalAlign: "top", color: "black" }}
                                        colSpan={8}
                                        valign="top"
                                    >
                                        <b>Terms And Conditions :</b>
                                        <ol style={{ margin: "4px 0", paddingLeft: "20px", lineHeight: 1.4 }}>
                                            {orderData?.OrderInvoices?.[0]?.ITermsAndConditions?.split("\r")
                                                .filter(line => line.trim() !== "")
                                                .map((line, index) => (
                                                    <li key={index}>{line.replace(/^\d+\./, "").trim()}</li>
                                                ))}
                                        </ol>

                                        <b>Remarks :</b>
                                        <div style={{ marginTop: "2px", lineHeight: 1.4 }}>
                                            {orderData?.OrderInvoices?.[0]?.SpecialInstructions || ""}
                                        </div>
                                    </TableCell>

                                    <TableCell
                                        colSpan={4}
                                        sx={{
                                            border: "1px solid #000", // outer border only

                                            p: 0,
                                        }}
                                    >
                                        <Table
                                            size="small"
                                            sx={{
                                                borderCollapse: "collapse",
                                                width: "100%",
                                                "& td, & th": { border: "0px solid #000", p: "2px 4px" }, // control border + padding
                                            }}
                                        >
                                            <TableBody>


                                                <TableCell colSpan={4} sx={{ border: "1px solid #000", p: 0 }}>
                                                    <Table
                                                        size="small"
                                                        sx={{
                                                            borderCollapse: "collapse",
                                                            width: "100%",
                                                            "& td, & th": { border: "0px solid #000", p: "2px 4px" },
                                                        }}
                                                    >
                                                        <TableBody>
                                                            {chargeRows.map((row, idx) => (
                                                                <TableRow key={idx}>
                                                                    <TableCell sx={{ border: "1px solid #000", color: "black" }}>
                                                                        {row.header}
                                                                    </TableCell>
                                                                    <TableCell
                                                                        sx={{ border: "1px solid #000", textAlign: "right", color: "black" }}
                                                                    >
                                                                        {Number(row.amount).toLocaleString("en-IN", {
                                                                            minimumFractionDigits: 2,
                                                                            maximumFractionDigits: 2,
                                                                        })}
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableCell>


                                            </TableBody>
                                        </Table>
                                    </TableCell>
                                </TableRow>


                                {/* Amount in Words + Total Order Value */}
                                <TableRow>
                                    <TableCell sx={{ border: "1px solid #000", color: "black" }} colSpan={8}>
                                        {/* Amount In Words : */}
                                        <b> {orderData?.OrderInvoices?.[0]?.INetAmountInWords}</b>
                                    </TableCell>
                                    <TableCell sx={{ border: "1px solid #000", fontWeight: "bold", textAlign: "right", color: "black" }} colSpan={4}>
                                        Total Order Value : {totals?.netAmount || '0.00'}
                                    </TableCell>
                                </TableRow>

                                {/* Bill-To / Ship-To / Confirmation */}
                                <TableRow>
                                    <TableCell sx={{ border: "1px solid #000", p: "4px", verticalAlign: "top", color: "black" }} colSpan={6} valign="top">
                                        <Grid container spacing={2} >
                                            <Grid item xs={12}>
                                                <Typography fontWeight="700" gutterBottom>
                                                    Bank Details :
                                                </Typography>

                                                <Grid container>
                                                    {orderData?.OrderInvoices?.[0]?.IBankDetails?.split("\r")
                                                        .filter(line => line.trim() !== "")
                                                        .map((line, index) => {
                                                            const [label, value] = line.split(":").map(s => s.trim());
                                                            return (
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                    key={index}
                                                                    sx={{ display: "flex", lineHeight: 1.4, mb: 0.2 }} // tighter spacing
                                                                >
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{ fontWeight: 600, minWidth: 110, lineHeight: 1.4 }}
                                                                    >
                                                                        {label}:
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{ ml: 1, lineHeight: 1.4 }}
                                                                    >
                                                                        {value}
                                                                    </Typography>
                                                                </Grid>
                                                            );
                                                        })}
                                                </Grid>
                                            </Grid>

                                        </Grid>
                                    </TableCell>

                                    <TableCell sx={{ border: "1px solid #000", p: "4px", verticalAlign: "top", color: "black" }} colSpan={6} valign="top">
                                        <Grid container spacing={2} >

                                            <Grid item xs={12}>
                                                <Typography fontWeight="700">
                                                    Please make sure that the details below are correct.
                                                </Typography>
                                                <Typography fontWeight="600" gutterBottom>
                                                    Contact Details to be given to Truck Driver:
                                                </Typography>

                                                <Grid container>
                                                    {orderData?.OrderInvoices?.[0]?.ITransporterDetails?.split("\r")
                                                        .filter(line => line.trim() !== "")
                                                        .map((line, index) => {
                                                            const [label, value] = line.split(":").map(s => s.trim());
                                                            return (
                                                                <Grid
                                                                    item
                                                                    xs={12}
                                                                    key={index}
                                                                    sx={{ display: "flex", lineHeight: 1.4, mb: 0.3 }}
                                                                >
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{ fontWeight: 600, minWidth: 150, lineHeight: 1.4 }}
                                                                    >
                                                                        {label}:
                                                                    </Typography>
                                                                    <Typography variant="body2" sx={{ ml: 1, lineHeight: 1.4 }}>
                                                                        {value}
                                                                    </Typography>
                                                                </Grid>
                                                            );
                                                        })}
                                                </Grid>

                                                {/* <Divider sx={{ my: 0.5 }} />
                                                <Typography fontWeight="700">Parent Party</Typography>
                                                <Typography fontWeight="700">AS PACKERS PROVIDERS (P) LTD</Typography>
                                                <Typography fontWeight="600">Email ID for Sending Invoice :</Typography>
                                                <Typography variant="body2"> {orderData?.contact?.email || "support@papyrus360.com"}</Typography> */}
                                            </Grid>
                                        </Grid>
                                    </TableCell>
                                </TableRow>

                                {/* Certification Line */}
                                <TableRow>
                                    <TableCell sx={{ border: "1px solid #000", textAlign: "center", color: "black" }} colSpan={12}>
                                        Certified that the particulars given above are true and correct and the amount indicated
                                        represents the price actually charged and that there is no flow of additional
                                        consideration directly or indirectly from the buyer.
                                    </TableCell>
                                </TableRow>

                                <TableRow>
                                    <TableCell sx={{ border: "1px solid #000", color: "black" }} colSpan={6} valign="top">
                                        Created By : {orderData?.createdBy || "Admin"} <br />
                                        Approved By : {orderData?.approvedBy || "Admin"}
                                    </TableCell>
                                    <TableCell
                                        sx={{ border: "1px solid #000", textAlign: "right", fontWeight: "bold", color: "black" }}
                                        colSpan={6}
                                        valign="top"
                                    >
                                        For {orderData?.companyName}
                                    </TableCell>
                                </TableRow>

                                {/* Page Info & Print Date */}
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ border: "none", color: "black" }}>
                                        Page 1 of 1
                                    </TableCell>
                                    <TableCell colSpan={6} align="right" sx={{ border: "none", color: "black" }}>
                                        Printed on {new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>

                    </Paper>
                </ResponsiveBox>
            </Box>

        </>
    );
};

export default SalesOrderReport;

